import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Award, Loader2, CheckCircle2 } from 'lucide-react';
import { useData } from '@happyspace/sdk/data';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const values = [
  { icon: Heart, title: 'Made with Love', desc: 'Every dish is prepared with care, channeling the warmth of homestyle cooking.' },
  { icon: Award, title: 'Authentic Recipes', desc: 'Traditional recipes passed down through generations, staying true to their roots.' },
  { icon: Users, title: 'Community First', desc: 'Building connections within the Indian diaspora and DFW food community.' },
];

export default function AboutPage() {
  const { insert, loading: submitting } = useData('contact_submissions');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await insert({ ...form, source: 'about-page', submittedAt: new Date().toISOString() });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-[#0A0F1E] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="text-[#D4AF37] text-sm font-bold tracking-widest uppercase">Our Story</span>
            <h1 className="text-5xl font-bold mt-3 mb-6">About Dil Se Desi</h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              "Dil Se Desi" — from the heart, truly Indian. We're a family-run restaurant in Plano, TX
              dedicated to bringing the soul of Indian home cooking to the DFW community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#0054e9] text-sm font-bold tracking-widest uppercase">The Beginning</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-6">A Celebration of Indian Culinary Heritage</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Dil Se Desi was founded with a singular mission: to make authentic, soulful Indian food accessible
                  to everyone in the DFW community. Our chefs bring decades of experience cooking traditional recipes
                  from every region of India.
                </p>
                <p>
                  Our menu celebrates the full spectrum of Indian cuisine — from aromatic biryanis and crispy appetizers
                  to hearty breakfasts, fresh breads, indulgent desserts, and refreshing shakes.
                </p>
                <p>
                  We believe food is more than sustenance — it's a cultural bridge, a memory, a celebration. Every
                  plate we serve carries the flavors of home.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-03-01/e567dfb649c9.png"
                alt="Our kitchen"
                className="rounded-3xl w-full h-56 object-cover col-span-2"
              />
              <img
                src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-02-21/e626af6874b6.png"
                alt="Our food"
                className="rounded-3xl w-full h-40 object-cover"
              />
              <img
                src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-02-21/3160d9f84e6b.png"
                alt="Our spices"
                className="rounded-3xl w-full h-40 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#0054e9] text-sm font-bold tracking-widest uppercase">What We Stand For</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3">Our Core Values</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#0054e9]/10 rounded-2xl flex items-center justify-center mb-6">
                  <v.icon className="w-7 h-7 text-[#0054e9]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{v.title}</h3>
                <p className="text-gray-500 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-white">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#0054e9] text-sm font-bold tracking-widest uppercase">Get in Touch</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-8">Send Us a Message</h2>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-3xl p-10 text-center">
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-600">Thank you, {form.name}. We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Name</label>
                    <input
                      required
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      className="w-full p-4 bg-gray-50 rounded-xl focus:ring-2 focus:ring-[#0054e9] outline-none border border-transparent focus:border-[#0054e9]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Email</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      className="w-full p-4 bg-gray-50 rounded-xl focus:ring-2 focus:ring-[#0054e9] outline-none border border-transparent focus:border-[#0054e9]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Message</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    className="w-full p-4 bg-gray-50 rounded-xl focus:ring-2 focus:ring-[#0054e9] outline-none border border-transparent focus:border-[#0054e9] resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0054e9] text-white py-4 rounded-full font-bold text-lg hover:bg-[#015796] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Message'}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
